import { useState, useRef } from "react";
import axiosInstance from "../utils/axiosInstance";

export type ReportStatus = "idle" | "pending" | "processing" | "done" | "error";

interface UseReportDownloadOptions {
  pollInterval?: number;
  maxRetries?: number;
  timeoutSeconds?: number;
}

interface ReportPayload {
  logo_b64?: string;
  job_config_id?: string;
  user_id?: string;
  job_type?: string;
}

export function useReportDownload({
  pollInterval = 2000,
  maxRetries = 3,
  timeoutSeconds = 120,
}: UseReportDownloadOptions = {}) {
  const [status, setStatus] = useState<ReportStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const pollerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);

  const isRunningRef = useRef(false);
  const downloadedRef = useRef(false);
  const activeJobIdRef = useRef<string | null>(null);

  const stopAll = () => {
    if (pollerRef.current) {
      clearTimeout(pollerRef.current);
      pollerRef.current = null;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    retryCountRef.current = 0;
    isRunningRef.current = false;
    activeJobIdRef.current = null;
  };

  const scheduleNextPoll = (jobId: string) => {
    pollerRef.current = setTimeout(() => {
      pollStatus(jobId);
    }, pollInterval);
  };

  const pollStatus = async (jobId: string) => {
    if (!isRunningRef.current || activeJobIdRef.current !== jobId) return;

    try {
      const poll = await axiosInstance.get(`/api/report/status/${jobId}`);

      if (poll.status === 404) {
        stopAll();
        setStatus("error");
        setError("Report session expired. Please try again.");
        return;
      }

      retryCountRef.current = 0;

      const data = poll.data;

      if (data.status === "done") {
        if (downloadedRef.current) return;

        downloadedRef.current = true;
        stopAll();
        setStatus("done");

        if (data.pdf_b64) {
          const bytes = Uint8Array.from(atob(data.pdf_b64), (c) => c.charCodeAt(0));
          const blob = new Blob([bytes], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);

          triggerDownload(url, "adrsta-incrementality-report.pdf");

          setTimeout(() => URL.revokeObjectURL(url), 1000);
          return;
        }

        if (data.url) {
          triggerDownload(data.url, "adrsta-incrementality-report.pdf");
          return;
        }

        setStatus("error");
        setError("Report is ready, but download URL was not found.");
        return;
      }

      if (data.status === "error") {
        stopAll();
        setStatus("error");
        setError(data.error || "Report generation failed");
        return;
      }

      scheduleNextPoll(jobId);
    } catch {
      retryCountRef.current += 1;

      if (retryCountRef.current >= maxRetries) {
        stopAll();
        setStatus("error");
        setError(`Please check your connection.`);
        return;
      }

      scheduleNextPoll(jobId);
    }
  };

  const downloadReport = async (payload?: ReportPayload) => {
    if (isRunningRef.current) {
      console.warn("[report] downloadReport called while report is already running — ignored");
      return;
    }

    isRunningRef.current = true;
    downloadedRef.current = false;

    setStatus("pending");
    setError(null);
    retryCountRef.current = 0;

    try {
      const res = await axiosInstance.post(`/api/report/generate`,
        payload || {},
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("authToken"),
          }
        }
      );

      const { job_id } = res.data;

      activeJobIdRef.current = job_id;
      setStatus("processing");

      timeoutRef.current = setTimeout(() => {
        stopAll();
        setStatus("error");
        setError("Report generation timed out. Please try again.");
      }, timeoutSeconds * 1000);

      pollStatus(job_id);
    } catch (err: any) {
      stopAll();
      setStatus("error");
      setError(err.message || "Unknown error");
    }
  };

  const reset = () => {
    stopAll();
    downloadedRef.current = false;
    setStatus("idle");
    setError(null);
  };

  return { status, error, downloadReport, reset };
}

function triggerDownload(url: string, filename: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}